import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TablePagination,
  MenuItem,
  CircularProgress,
  Card,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AddIcon from '@mui/icons-material/Add';
import { ReactComponent as SampleFileIcon } from "../../assets/svg/sample_file.svg";
import { ReactComponent as AddDeviceIcon } from '../../assets/svg/add_new_device-2.svg';
import { ReactComponent as BulkUploadIcon } from '../../assets/svg/bulk_upload.svg';
import DeviceDialog from './AddNewDeviceDialog';
import DeleteDialog from './DeleteDialog';
import EditDeviceDialog from './EditDeviceDialog';
import BulkUploadDialog from './BulkUploadDialog';
import { useQuery, gql, useLazyQuery } from '@apollo/client';
import { useAuth } from '../../hooks/useAuth';
const GET_ALL_DEVICES = gql`
  query GetAllDevices($pageSize: String!, $page: String!) {
    getAllDevices(pageSize: $pageSize, page: $page) {
      totalCount
      data {
        tcu_id
        imei
        serial_number
        tcu_make
        tcu_model
        created_on
      }
    }
  }
`;
const SEARCH_DEVICE = gql`
query SearchDevice($searchType: String!, $searchItem: String!) {
  searchDevice(searchType: $searchType, searchItem: $searchItem) {
    tcu_id
    imei
    serial_number
    tcu_make
    tcu_model
    created_on
  }
}
`;
const DOWNLOAD_TCU_INVENTORY = gql`
  query DownloadTcuInventory {
    downloadTcuInventory {
      url
    }
  }
`;
const cellStyle = {
  fontSize: '14px',
  padding: '8px',
  width: 'auto',
  borderBottom: '1px solid #e0e0e0',
  borderRight: '1px solid #e0e0e0',
  color: '#000',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const DeviceManager = () => {
  const auth = useAuth();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("imei");
  const [totalCount, setTotalCount] = useState(0);
  const { loading, error, data, refetch } = useQuery(GET_ALL_DEVICES, {
    variables: {
      pageSize: rowsPerPage.toString(),
      page: (page + 1).toString() // Adding 1 because backend typically starts at 1
    },
    fetchPolicy: 'cache-and-network'
  });
  const [searchDevices, { data: searchData, loading: searchLoading }] = useLazyQuery(SEARCH_DEVICE);
  const [downloadInventory, { loading: downloadLoading }] = useLazyQuery(DOWNLOAD_TCU_INVENTORY);
  const [deviceData, setDeviceData] = useState<any[]>([]);
  const formatEpoch = (epochTime: number): string => {
    const date = new Date(epochTime * 1000);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };
  useEffect(() => {
    if (data?.getAllDevices) {
      setDeviceData(data.getAllDevices.data);
      setTotalCount(data.getAllDevices.totalCount);
    }
  }, [data]);
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchTerm.trim().length > 0 && ["imei", "serial_number"].includes(searchType)) {
        setPage(0); // Reset to first page when searching
        searchDevices({ variables: { searchType, searchItem: searchTerm.trim() } });
      } else {
        // When clearing search, show the default table data without refetching
        if (data?.getAllDevices) {
          setDeviceData(data.getAllDevices.data);
          setTotalCount(data.getAllDevices.totalCount);
        }
      }
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, searchType, rowsPerPage, data]);
  useEffect(() => {
    if (searchData?.searchDevice) {
      setDeviceData(searchData.searchDevice);
      setTotalCount(searchData.searchDevice.length); // For search results, use the actual count
    }
  }, [searchData]);
  const handleExportToExcel = async () => {
    try {
      const { data } = await downloadInventory();
      if (data?.downloadTcuInventory?.url) {
        const link = document.createElement('a');
        link.href = data.downloadTcuInventory.url;
        link.setAttribute('download', 'Device_List.xlsx');
        document.body.appendChild(link);
        link.click();
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      }
    } catch (error) {
      console.error('Error downloading inventory:', error);
    }
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (searchTerm.trim().length === 0) {
      refetch({
        pageSize: newRowsPerPage.toString(),
        page: '1'
      });
    }
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    if (searchTerm.trim().length === 0) {
      refetch({
        pageSize: rowsPerPage.toString(),
        page: (newPage + 1).toString()
      });
    }
  };
  const handleDownloadSample = () => {
    const link = document.createElement("a");
    link.href = "/Device-Upload-Sample.xlsx";
    link.download = "Device-Upload-Sample.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const tableColumns = [
    "Sr.No",
    "Device Model",
    "Device IMEI",
    "Device Serial",
    "Device Make",
    "Created On",
    ...(auth.canCreate("TCU") || auth.canUpdate("TCU") || auth.canDelete("TCU") ? ["Action"] : [])
  ];
  // Use different data sources based on whether we're searching or not
  const tableData = searchTerm.trim().length > 0
    ? deviceData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : deviceData;
  // Use different counts based on whether we're searching or not
  const paginationCount = searchTerm.trim().length > 0 ? totalCount : totalCount;
  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      <Typography variant="h5" fontSize={{ xs: '1.5rem', sm: '2.125rem' }} sx={{ mb: 2, borderRadius: 2 }}>
        Device Manager
      </Typography>
      <Card sx={{
        width: '100%',
        maxWidth: '100vw',
        mx: 'auto',
        boxShadow: 2,
        borderRadius: 3,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(100vh - 120px)' },
        bgcolor: '#fff',
        minHeight: 100,
      }}>
        {/* Sticky header controls */}
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: '#fff',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          boxShadow: '0 2px 8px -4px #e0e0e0',
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 2,
          left: 0,
          right: 0,
          width: '100%',
          minWidth: 0,
          overflow: 'visible',
        }}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}
          >
            <Box
              display="flex"
              gap={1}
              alignItems="center"
              sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '100%', sm: '40%' }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
            >
              <TextField
                select
                size="small"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                sx={{ minWidth: 120, background: '#f5f5f5', flex: '0 0 auto' }}
              >
                <MenuItem value="imei">Search by IMEI</MenuItem>
                <MenuItem value="serial_number">Search by Serial No</MenuItem>
              </TextField>
              <TextField
                size="small"
                placeholder={
                  searchType === 'imei' ? 'Enter IMEI' :
                    searchType === 'serial_number' ? 'Enter Serial No' : ''
                }
                variant="outlined"
                sx={{ minWidth: 120, background: '#f5f5f5', flex: 1 }}
                inputProps={{
                  maxLength: searchType === 'imei' ? 15 : 30
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => {
                  let value = e.target.value;
                  if (searchType === 'imei') {
                    value = value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 15);
                  } else {
                    value = value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 30);
                  }
                  setSearchTerm(value);
                }}
              />
              <IconButton
                sx={{ ml: 1, color: '#000', flex: '0 0 auto' }}
                onClick={handleExportToExcel}
                disabled={downloadLoading}
              >
                <Tooltip title="Export to Excel" placement="top" arrow>
                  {downloadLoading ? <CircularProgress size={24} /> : <CloudDownloadIcon />}
                </Tooltip>
              </IconButton>
            </Box>
            {(auth.canCreate("TCU") || auth.canUpdate("TCU")) && (
              <Box display="flex" gap={2} flexWrap={{ xs: 'wrap', sm: 'nowrap' }} sx={{ mt: { xs: 1, sm: 0 }, ml: { xs: 0, sm: 'auto' } }}>
                {(auth.canUpdate("TCU") || auth.canCreate("TCU")) && (
                  <Button
                    startIcon={<SampleFileIcon style={{ width: 24, height: 24 }} />}
                    sx={{
                      textTransform: 'none',
                      backgroundColor: '#000',
                      color: '#fff',
                      borderRadius: '8px',
                      paddingX: 2,
                      paddingY: 1,
                      minWidth: 120,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      '&:hover': {
                        backgroundColor: '#222',
                      },
                      '.MuiButton-startIcon': {
                        color: '#fff',
                      }
                    }}
                    onClick={handleDownloadSample}
                  >
                    Sample File
                  </Button>
                )}
                {auth.canCreate("TCU") && (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<AddDeviceIcon />}
                      sx={{
                        color: '#fff',
                        backgroundColor: '#E60012',
                        '&:hover': { backgroundColor: '#cc0010' },
                        textTransform: 'none',
                        borderRadius: '8px',
                        paddingX: 2,
                        paddingY: 1,
                        minWidth: 120,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                      }}
                      onClick={() => setAddDialogOpen(true)}
                    >
                      Add New Device
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<BulkUploadIcon />}
                      sx={{
                        color: '#fff',
                        backgroundColor: '#E60012',
                        '&:hover': { backgroundColor: '#cc0010' },
                        textTransform: 'none',
                        borderRadius: '8px',
                        paddingX: 2,
                        paddingY: 1,
                        minWidth: 120,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                      }}
                      onClick={() => setBulkDialogOpen(true)}
                    >
                      Bulk Upload
                    </Button>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
        {/* Table area */}
        <Box sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 0.5, sm: 2 },
          py: 2,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                borderRadius: 2,
                width: '100%',
                minWidth: 600,
                background: '#fff',
                overflow: 'visible',
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 600, width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#E60012' }}>
                    {tableColumns.map((header) => (
                      <TableCell
                        key={header}
                        align="center"
                        sx={{
                          backgroundColor: '#E60012',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          whiteSpace: 'nowrap',
                          padding: { xs: '4px', sm: '6px' },
                          borderBottom: '1px solid #e0e0e0',
                          borderRight: '1px solid #e0e0e0',
                          position: 'sticky',
                          top: 0,
                          zIndex: 11,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(loading || searchLoading) ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : tableData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ fontSize: '1.1rem', color: '#888', py: 4 }}>
                        No devices found
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((device, idx) => (
                      <TableRow
                        key={device.tcu_id}
                        hover
                        sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5' }}
                      >
                        <TableCell align="center" sx={cellStyle}>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{device.tcu_model}</TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...cellStyle,
                            maxWidth: 200,
                            whiteSpace: 'nowrap',
                            fontSize: device.imei && device.imei.length > 20 ? '0.85rem' : '0.9rem',
                          }}
                          title={device.imei}
                        >
                          {device.imei}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...cellStyle,
                            minWidth: 200,
                            whiteSpace: 'nowrap',
                          }}
                          title={device.serial_number}
                        >
                          {device.serial_number}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{device.tcu_make}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatEpoch(device.created_on)}</TableCell>
                        {(auth.canCreate("TCU") || auth.canUpdate("TCU") || auth.canDelete("TCU")) && (
                          <TableCell align="center" sx={cellStyle}>
                            <Box display="flex" justifyContent="center" gap={3}>
                              {auth.canUpdate("TCU") && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedDevice(device);
                                    setEditDialogOpen(true);
                                  }}
                                  sx={{ color: '#000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              {auth.canDelete("TCU") && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedDevice(device);
                                    setDeleteDialogOpen(true);
                                  }}
                                  sx={{ color: '#000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
        {/* Pagination */}
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          background: '#fff',
          borderTop: '1px solid #e0e0e0',
          pt: 2,
          mt: 'auto',
          overflowX: 'auto',
        }}>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500]}
            component="div"
            count={paginationCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            sx={{
              minWidth: '600px',
              '.MuiTablePagination-toolbar': {
                flexWrap: 'nowrap',
                justifyContent: { xs: 'center', sm: 'space-between' },
              },
            }}
          />
        </Box>
      </Card>
      {/* Dialogs */}
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          setDeleteDialogOpen(false);
          refetch();
        }}
        deviceId={selectedDevice?.tcu_id || ''}
      />
      <DeviceDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSubmit={() => {
          setAddDialogOpen(false);
          refetch();
        }}
      />
      <EditDeviceDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        refetch={refetch}
        onSave={() => {
          setEditDialogOpen(false);
        }}
        device={
          selectedDevice
            ? {
              imei: selectedDevice.imei,
              tcuId: selectedDevice.tcu_id,
              serialNumber: selectedDevice.serial_number,
              tcuMake: selectedDevice.tcu_make,
              tcuModel: selectedDevice.tcu_model,
            }
            : null
        }
      />
      <BulkUploadDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        onUploaded={() => refetch()}
      />
    </Box>
  );
};
export default DeviceManager;